import type { Locale } from "../../i18n/locales";

export type ProfileBlock = { p: string } | { h: string } | { ul: string[] };

export type DiseaseProfile = {
  updated: string;
  blocks: ProfileBlock[];
};

const nl: Record<string, DiseaseProfile> = {
  buiktyfus: {
    updated: "2024-04-02",
    blocks: [
      { p: "Buiktyfus is een infectie door de bacterie Salmonella typhi." },
      {
        p: "Je kunt buiktyfus krijgen in Azië en Afrika. De ziekte komt ook voor in Centraal- en Zuid-Amerika, maar daar is het risico laag. In Nederland komt de ziekte bijna niet voor.",
      },
      {
        p: "Je kunt buiktyfus krijgen als je voedsel eet of water drinkt dat is besmet met de bacterie Salmonella typhi. Voedsel of water kan besmet raken doordat de ontlasting van een besmet persoon hierin terechtkomt. Dit hoeft niet zichtbaar te zijn. Als een besmet persoon zijn of haar handen niet wast na het toilet en jouw eten klaarmaakt, kun je al buiktyfus krijgen. Daarnaast kun je de ziekte krijgen als je iets eet dat niet gaar of gekookt genoeg is, zoals vlees, kip of eieren, of als je rauwe melkproducten drinkt. Als je de ziekte hebt, kun je ook andere mensen besmetten.",
      },
      {
        p: "Bij deze ziekte heb je koorts, hoofdpijn, buikpijn en minder zin om te eten. Soms krijg je diarree, afgewisseld met verstopping. Later kun je suf worden en rode vlekken op je buik krijgen. Je kunt erg ziek zijn en eraan overlijden.",
      },
      {
        p: "Het kan verstandig zijn om een vaccinatie te nemen. Of dat nodig is, hangt af van jouw gezondheid, waar je heen gaat en hoelang je gaat. Vraag bij een reizigersvaccinatiebureau of een vaccinatie voor jouw reis nodig is.",
      },
      {
        p: "Een vaccinatie beschermt niet volledig. Je maakt de kans op buiktyfus kleiner met goede hygiëne, vooral als je eten maakt. Was regelmatig je handen met zeep, zeker na het toilet. Drink alleen gekookt water of water uit flessen en eet voedsel dat goed en schoon is klaargemaakt.",
      },
    ],
  },
  chikungunya: {
    updated: "2026-04-30",
    blocks: [
      {
        p: "Chikungunya is een infectieziekte door een virus. De ziekte lijkt veel op dengue (knokkelkoorts).",
      },
      {
        p: "Je kunt chikungunya krijgen in stedelijke gebieden in veel landen met een warm of (sub)tropisch klimaat.",
      },
      {
        p: "Je kunt de ziekte krijgen als je wordt geprikt door een mug met chikungunya. Dat gebeurt vooral overdag, tussen zonsopkomst en zonsondergang. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "Als je zwanger bent kun je het virus, vooral vlak voor of tijdens de bevalling, doorgeven aan je baby. De baby kan dan ernstig ziek worden. Het wordt afgeraden om in het laatste trimester van je zwangerschap te reizen naar een land waar een grote uitbraak van chikungunya is.",
      },
      {
        p: "Chikungunya lijkt erg op griep met koorts, hoofdpijn, spier- en rugpijn, misselijkheid en braken. Ook kun je huiduitslag krijgen en pijn in de gewrichten. Deze ziekteverschijnselen zijn meestal na één tot drie weken weer weg. Pijn in de gewrichten kan soms maanden tot jaren duren. Overlijden komt nauwelijks voor. Als je weer beter bent, kun je niet nog een keer ziek worden van het chikungunya-virus.",
      },
      {
        p: "Houd overdag muggen op afstand. In Nederland is het sinds april 2025 mogelijk om je te laten vaccineren tegen chikungunya. Voor sommige reizigers kan een vaccinatie zinvol zijn. Vraag bij een reizigersvaccinatiebureau wat er voor jou mogelijk is.",
      },
    ],
  },
  dengue: {
    updated: "2026-08-13",
    blocks: [
      {
        p: "Dengue is een infectieziekte die wordt veroorzaakt door een virus. De ziekte heet ook wel knokkelkoorts.",
      },
      {
        p: "Je kunt dengue krijgen in stedelijke gebieden in veel landen met een warm of (sub)tropisch klimaat. Vooral tijdens het regenseizoen neemt de kans op ziekte toe en kunnen er grote uitbraken ontstaan. De laatste 50 jaar is het aantal denguebesmettingen wereldwijd sterk toegenomen.",
      },
      {
        p: "Je kunt de ziekte krijgen als je wordt geprikt door een mug die het denguevirus bij zich draagt. Dat gebeurt vooral overdag. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "Dengue lijkt erg op griep en verloopt meestal onschuldig met koorts, hoofdpijn (achter de ogen), spierpijn en gewrichtspijn. Na enkele dagen kun je huiduitslag krijgen. Heel soms is de ziekte ernstig, met bloedingen, en dan kan een ziekenhuisopname nodig zijn. De kans op ernstige ziekte is licht verhoogd als je al één keer dengue hebt doorgemaakt.",
      },
      {
        p: "Houd overdag muggen op afstand. Voor volwassenen en kinderen ouder dan vier jaar is er een vaccin tegen dengue. Vraag bij een reizigersvaccinatiebureau wat er voor jou mogelijk is.",
      },
    ],
  },
  dtp: {
    updated: "2023-12-07",
    blocks: [
      { p: "DTP is de afkorting van difterie, tetanus en polio." },
      { h: "Difterie" },
      {
        p: "Difterie (ook wel kroep genoemd) is een infectieziekte door de bacterie Corynebacterium diphtheriae. Je kunt de ziekte krijgen van mensen die de bacterie in hun keel of op hun huid hebben, via hoesten of wondvocht.",
      },
      {
        p: "De bacterie maakt gifstoffen die je keel dikker maken, waardoor je moeilijk kunt ademhalen. Als de gifstoffen in het bloed komen, kan je hart ontstoken raken. Daaraan kun je overlijden. Een ontsteking van de huid is minder ernstig.",
      },
      { h: "Tetanus" },
      {
        p: "Tetanus is een infectieziekte door de bacterie Clostridium tetani. De bacterie leeft in aarde en straatvuil. Je krijgt die via een wond of via je slijmvliezen. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "Als de bacterie in een wond komt, kunnen gifstoffen spierkrampen in je hele lichaam veroorzaken. Kramp in de ademhalingsspieren kan dodelijk zijn.",
      },
      { h: "Polio" },
      {
        p: "Polio (kinderverlamming) komt door besmetting met het poliovirus. Je kunt het virus krijgen van andere mensen, via ontlasting of door eten en drinken dat door ontlasting is besmet.",
      },
      {
        p: "Meestal zijn er geen kenmerken. Soms voel je je grieperig. Bij een klein deel van de besmettingen kan tijdelijke verlamming optreden, meestal aan de benen, soms aan de ademhalingsspieren. Heel soms blijft de verlamming: bij ongeveer één op de tweehonderd mensen die polio krijgen.",
      },
      {
        p: "Vrijwel iedereen die na 1950 in Nederland is geboren, is als kind gevaccineerd tegen deze ziekten. Is het langer dan tien jaar geleden dat je voor het laatst gevaccineerd bent? Neem dan een herhaalprik met het DTP-vaccin. Vraag advies bij een reizigersvaccinatiebureau.",
      },
    ],
  },
  "gele-koorts": {
    updated: "2026-03-16",
    blocks: [
      {
        p: "Gele koorts is een infectieziekte die wordt veroorzaakt door een virus.",
      },
      {
        p: "Je kunt gele koorts krijgen in bepaalde delen van Afrika en Zuid-Amerika.",
      },
      {
        p: "Mensen en apen kunnen het virus hebben. Je kunt het virus krijgen als je wordt gestoken door een mug. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "Je kunt je grieperig voelen met koorts en soms geelzucht, waarbij je huid en oogwit geel zijn. Je kunt ook ernstig ziek worden en aan het virus overlijden. 3 tot 20 procent van de mensen die gele koorts krijgen, overlijdt aan de ziekte.",
      },
      {
        p: "Je kunt je laten vaccineren tegen gele koorts. Voor sommige landen is deze vaccinatie verplicht. Als een vaccinatie verplicht is, moet je die minimaal tien dagen voordat je aankomt hebben gekregen. Ook als een vaccinatie niet verplicht is, adviseert het LCR vaccinatie als je naar een gebied reist waar gele koorts voorkomt.",
      },
      {
        p: "Niet elk reizigersvaccinatiebureau mag tegen gele koorts vaccineren: dat mag alleen als een bureau aan de eisen in de wet voldoet. Vraag daarom altijd om advies bij een reizigersvaccinatiebureau.",
      },
    ],
  },
  "hepatitis-a": {
    updated: "2026-07-09",
    blocks: [
      {
        p: "Hepatitis A is een ontsteking van de lever door het hepatitis A-virus. Het is een heel besmettelijk virus.",
      },
      {
        p: "Je kunt hepatitis A krijgen in Afrika, Midden- en Zuid-Amerika en Azië, maar ook in landen in Oost-Europa. In Nederland komt de ziekte bijna niet voor.",
      },
      {
        p: "Je kunt hepatitis A krijgen door water te drinken of iets te eten dat besmet is met ontlasting van iemand die hepatitis A heeft. Ook via besmette deurklinken, kranen en handdoekjes kan het virus worden overgedragen. Je kunt indirect andere mensen besmetten.",
      },
      {
        p: "Volwassenen en oudere kinderen kunnen moe en misselijk zijn, koorts en buikpijn hebben en minder zin om te eten. Na enkele dagen kan er geelzucht ontstaan: de huid en het oogwit worden geel. Je hebt meestal een paar weken klachten, soms tot drie maanden. Kleine kinderen worden vaak niet zo ziek, maar kunnen het virus wel overdragen.",
      },
      {
        p: "Voor de meeste landen in de tropen en subtropen, en in een aantal landen in Oost-Europa en rond de Middellandse Zee, is het verstandig om een vaccinatie te nemen. Vraag bij een reizigersvaccinatiebureau of een vaccinatie voor jouw reis nodig is.",
      },
      { p: "Je maakt de kans op hepatitis A kleiner met deze maatregelen:" },
      { h: "Goede hygiëne" },
      {
        ul: [
          "Zorg dat alles zo schoon mogelijk is en was regelmatig je handen met zeep, zeker na het toilet en vóór het eten.",
        ],
      },
      { h: "Eten en drinken" },
      {
        ul: [
          "Drink alleen water uit verzegelde flessen of gekookt water.",
          "Gebruik geen ijsklontjes: die worden vaak van kraanwater gemaakt.",
          "Eet alleen verpakt ijs.",
          "Eet geen rauwkost of salades: die zijn vaak met kraanwater gewassen.",
          "Schil of pel zelf je fruit.",
          "Eet uitsluitend vers en goed verhit vlees of vis, inclusief schaal- en schelpdieren.",
        ],
      },
      {
        p: "Omdat hepatitis A heel besmettelijk is, zijn deze maatregelen niet altijd voldoende.",
      },
    ],
  },
  "hepatitis-b": {
    updated: "2024-05-02",
    blocks: [
      {
        p: "Hepatitis B is een ontsteking van de lever door het hepatitis B-virus.",
      },
      {
        p: "Hepatitis B komt overal voor, maar vooral in Afrika, Azië, Oost-Europa en Latijns-Amerika.",
      },
      { p: "Je kunt de ziekte oplopen via:" },
      {
        ul: [
          "seks zonder condoom of ander beschermingsmiddel",
          "contact met bloed, bijvoorbeeld via drugsspuiten",
          "een medische behandeling die niet steriel is, bijvoorbeeld bij de tandarts",
          "tatoeages en piercings",
          "scheren met een mesje dat al gebruikt is",
        ],
      },
      {
        p: "In landen waar hepatitis B vaker voorkomt, kunnen kinderen het bij de geboorte via hun moeder oplopen. Als je besmet bent, kun je anderen besmetten via onbeschermde seks of bloedcontact.",
      },
      {
        p: "Na een infectie hebben mensen vaak geen of weinig klachten, maar kunnen ze wel anderen besmetten. Klachten komen meestal pas twee tot zes maanden na besmetting: je voelt je grieperig en soms krijg je geelzucht. Sommige mensen blijven langdurig besmet. Zij merken daar vaak niets van, maar hebben een grotere kans op levercirrose (verlittekening van de lever) of leverkanker.",
      },
      {
        p: "Ben je lang in een gebied waar veel hepatitis B is, dan is het risico groter. In dat geval kun je een vaccin nemen. Vraag bij een reizigersvaccinatiebureau of een vaccinatie voor jouw reis nodig is.",
      },
    ],
  },
  hoogteziekte: {
    updated: "2026-03-16",
    blocks: [
      {
        p: "Stijg je te snel naar grote hoogte zonder te acclimatiseren, dan kun je hoogteziekte krijgen. Het lichaam krijgt dan niet voldoende tijd om zich aan het lage zuurstofgehalte in de lucht aan te passen.",
      },
      {
        p: "Dat gebeurt meestal pas boven de 2500 meter. Of je hoogteziekte krijgt, verschilt per persoon en heeft niet te maken met hoe fit je bent. Heb je eerder hoogteziekte gehad, dan is de kans groter dat je er weer last van krijgt.",
      },
      { p: "Er zijn twee vormen: een milde en een ernstige." },
      {
        ul: [
          "De milde vorm krijg je een paar uur of een dag nadat je op grote hoogte aankomt. Je bent kortademig, hebt hoofdpijn, bent misselijk en moet overgeven. Je hebt last van duizeligheid en voelt je zwak. Die klachten duren meestal een paar dagen, maar kunnen overgaan in de ernstige vorm.",
          "Bij de ernstige vorm worden hoofdpijn en kortademigheid erger, ook in rust en vooral ’s nachts. Je kunt zo zwak worden dat je hulp nodig hebt bij aankleden en eten. Als je loopt, kan het lijken alsof je dronken bent. Zonder behandeling kun je vocht in de longen en hersenen krijgen, in coma raken en overlijden.",
        ],
      },
      { p: "Je kunt het risico kleiner maken:" },
      {
        ul: [
          "Stijg langzaam naar een hoogte boven de 2500 meter.",
          "Laat je lichaam twee dagen wennen als je hoger dan 2500 meter stijgt.",
          "Gebruik geen alcohol of slaapmiddelen.",
          "Slaap bij voorkeur niet meer dan 300 meter hoger dan de nacht ervoor.",
          "In overleg met een arts kun je acetazolamide gebruiken als je eerder hoogteziekte hebt gehad, of als je plotseling moet stijgen naar een slaaphoogte boven de 3500 meter zonder te kunnen acclimatiseren.",
        ],
      },
      {
        p: "Bij de milde vorm: stijg niet verder of daal af als je niet binnen een paar dagen beter wordt, of als de ziekte erger wordt. Tegen hoofdpijn kun je paracetamol gebruiken. Bij de ernstige vorm moet je meteen afdalen. Daarmee kun je je leven redden. Een zorgverlener kan medicijnen of zuurstof geven.",
      },
    ],
  },
  "japanse-encefalitis": {
    updated: "2023-12-14",
    blocks: [
      {
        p: "Japanse encefalitis is een infectieziekte die wordt veroorzaakt door een virus.",
      },
      {
        p: "Je kunt Japanse encefalitis krijgen op het platteland in Azië en in delen van Australië.",
      },
      {
        p: "Je kunt de ziekte krijgen als je wordt geprikt door een mug met Japanse encefalitis. Die muggen prikken in de avond en nacht. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "De ziekte verloopt meestal mild en lijkt op griep met koorts, hoofdpijn en darmklachten. Heel soms is de ziekte erger en krijg je een ontsteking in de hersenen (encefalitis). Dan is er kans dat je invalide wordt of eraan overlijdt.",
      },
      {
        p: "Houd in de avond en nacht muggen op afstand met muggenwering, beschermende kleren en een klamboe. Er is een vaccin, maar dat krijg je alleen wanneer je langdurig risico loopt. Vraag bij een reizigersvaccinatiebureau of een vaccinatie voor jouw reis nodig is.",
      },
    ],
  },
  malaria: {
    updated: "2026-04-13",
    blocks: [
      {
        p: "Malaria is een infectieziekte die wordt veroorzaakt door een malariaparasiet.",
      },
      {
        p: "Je kunt malaria krijgen in (sub)tropische gebieden in Afrika, Azië en Midden- en Zuid-Amerika.",
      },
      {
        p: "Als je wordt geprikt door een mug met malaria, kunnen parasieten je lichaam binnenkomen. Die muggen prikken in de avond en nacht, tussen zonsondergang en zonsopgang. De parasieten vermenigvuldigen zich eerst in de lever en daarna in de rode bloedcellen. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "Malaria begint met koorts, vermoeidheid, hoofdpijn en spierpijn. De koorts is niet altijd hoog en je gaat er niet altijd van rillen. De ziekte kan op griep lijken. Je kunt ook diarree, overgeven, hoesten of kortademigheid krijgen.",
      },
      { p: "Er zijn vijf vormen van malaria:" },
      {
        ul: [
          "Malaria tropica, door Plasmodium falciparum, is het gevaarlijkst. Daaraan kun je in een paar dagen overlijden. Over het algemeen is die vorm goed te behandelen als de behandeling op tijd start.",
          "Plasmodium vivax, Plasmodium ovale en Plasmodium malariae zijn minder gevaarlijk. De kenmerken zijn hetzelfde, maar mensen overlijden hier zelden aan.",
          "Plasmodium knowlesi komt vooral bij makaken voor, maar soms ook bij mensen. Mensen kunnen aan deze vorm overlijden.",
        ],
      },
      {
        p: "Je kunt malaria tropica krijgen tot ongeveer vier weken nadat je besmet bent. Daarom moeten de meeste middelen tegen malaria ten minste vier weken na vertrek uit een malariagebied worden doorgebruikt. Atovaquon/proguanil hoef je maar één week na vertrek te gebruiken. Word je ziek, vertel de huisarts dan dat je kortgeleden in het buitenland bent geweest.",
      },
      {
        p: "Er bestaat geen effectief vaccin tegen malaria. Houd muggen op afstand. In gebieden met hoger risico is het advies om ook tabletten (chemoprofylaxe) te nemen, of soms een noodbehandeling mee te nemen. Vraag advies bij een reizigersvaccinatiebureau.",
      },
    ],
  },
  mazelen: {
    updated: "2026-07-22",
    blocks: [
      {
        p: "Mazelen is een zeer besmettelijke infectieziekte door het mazelenvirus. Eén persoon met mazelen kan 12 tot 18 anderen besmetten.",
      },
      {
        p: "Mazelen komt overal ter wereld voor. In de meeste landen krijgen kinderen een vaccinatie tegen de ziekte.",
      },
      {
        p: "Je kunt het virus via de lucht krijgen van iemand die hoest of niest. Het virus kan tot twee uur in de lucht blijven nadat iemand een ruimte heeft verlaten. Afstand houden helpt niet genoeg. Frisse lucht maakt de kans kleiner, maar niet nul.",
      },
      {
        p: "Mazelen begint meestal met koorts. Je voelt je hangerig, bent verkouden en moet hoesten. Er verschijnen kleine witte vlekjes in je mondslijmvlies. Na drie tot zeven dagen verschijnen vlekjes achter je oren en daarna over je hele lichaam. Soms is de ziekte ernstiger, met longontsteking of hersenontsteking, en kun je eraan overlijden.",
      },
      {
        p: "De meeste Nederlanders zijn als kind gevaccineerd in het Rijksvaccinatieprogramma, of hebben mazelen gehad. Ben je niet gevaccineerd en heb je nooit mazelen gehad, dan is het verstandig om het BMR-vaccin te nemen (bof, mazelen en rodehond).",
      },
      {
        p: "Ben je geboren tussen 1965 en 1975, dan heb je een grotere kans op mazelen: je bent geboren voordat de vaccinatie in het Rijksvaccinatieprogramma zat. Ga je naar een land waar meer mazelen is, dan is het verstandig om het BMR-vaccin te nemen.",
      },
      {
        p: "Kinderen jonger dan veertien maanden hebben nog geen BMR-vaccinatie gehad. Als zij naar een land gaan waar meer mazelen is dan in Nederland, kunnen zij in sommige gevallen vanaf 6 maanden een BMR-vaccin krijgen. Vraag advies bij een consultatiebureau of reizigersvaccinatiebureau.",
      },
    ],
  },
  meningokokken: {
    updated: "2024-04-04",
    blocks: [
      {
        p: "Meningokokken-meningitis (hersenvliesontsteking) is een ziekte door de bacterie Neisseria meningitidis (meningokok). De bekendste typen zijn A, B, C, W en Y.",
      },
      {
        p: "De ziekte komt overal voor. In Sub-Sahara-Afrika zijn er in het droge seizoen epidemieën, vooral door type A en steeds vaker type W135. Ook elders zijn er af en toe epidemieën.",
      },
      {
        p: "Je kunt de ziekte via de lucht krijgen van iemand die praat, hoest of niest, of via rechtstreeks contact, bijvoorbeeld zoenen op de mond.",
      },
      {
        p: "Als je ziek wordt, is dat meestal een paar dagen na besmetting. Het begint grieperig met hoge koorts, spierpijn, overgeven, hoofdpijn en nekstijfheid: pijn in je nek als je je hoofd buigt (nekkramp). De ziekte kan heel ernstig zijn, en soms dodelijk.",
      },
      {
        p: "Er is een vaccin tegen de typen A, C, W135 en Y (ACW135Y). Sinds mei 2018 krijgen kinderen van 14 maanden en 14 jaar deze vaccinatie in het Rijksvaccinatieprogramma. Vraag bij een reizigersvaccinatiebureau of vaccinatie nodig is, bijvoorbeeld bij een langer verblijf in een land waar vaak meningitis is. Voor reizigers op bedevaart naar Mekka is deze vaccinatie verplicht.",
      },
    ],
  },
  muggen: {
    updated: "2025-01-27",
    blocks: [
      {
        p: "Muggen kunnen je besmetten met ziekten als je op reis wordt geprikt.",
      },
      {
        p: "Ziekten die je via muggen kunt krijgen zijn onder andere gele koorts, malaria, dengue (knokkelkoorts), Japanse encefalitis, westnijlvirus, chikungunya, Eastern equine encephalitis, Barmah Forest-virus, Murray Valley-encefalitis, Ross River-virus, Saint Louis-encefalitis, zikakoorts en Oropouche-virus.",
      },
      {
        p: "Welke muggenziekten voor jouw reis spelen, zie je in het LCR-advies per land. Houd muggen op afstand met werende middelen, bedekkende kleding en waar nodig een klamboe.",
      },
    ],
  },
  pest: {
    updated: "2023-12-04",
    blocks: [
      {
        p: "De pest is een besmettelijke ziekte door de bacterie Yersinia pestis. Er zijn drie vormen: builenpest, longpest en sepsis (bloedvergiftiging). Welke vorm je krijgt, hangt af van hoe je besmet raakt.",
      },
      {
        p: "Je kunt de pest krijgen in Afrika, Noord- en Zuid-Amerika en Azië.",
      },
      {
        p: "De meeste mensen krijgen de pest door een beet van een besmette vlo. Je kunt de ziekte ook krijgen via besmette (knaag)dieren of mensen. Longpest kun je krijgen als je de bacterie inademt wanneer een besmet iemand hoest of niest.",
      },
      {
        p: "Meestal is het builenpest: koorts, koude rillingen, hoofdpijn en pijnlijke builen in liezen, oksels of hals. Zonder behandeling kun je bloedvergiftiging krijgen; de kans dat je daaraan overlijdt is groot. Longpest is de ernstigste vorm en lijkt op een erge longontsteking.",
      },
      {
        p: "Je kunt je niet laten vaccineren tegen de pest. Vermijd contact met zieke of dode dieren. Heb je huisdieren, zorg dan dat die geen vlooien krijgen.",
      },
    ],
  },
  rabies: {
    updated: "2025-08-07",
    blocks: [
      {
        p: "Rabiës of hondsdolheid is een zeldzame, dodelijke infectieziekte door een virus.",
      },
      { p: "Je kunt rabiës in veel landen krijgen." },
      {
        p: "Je kunt rabiës krijgen via speeksel van besmette zoogdieren: niet alleen honden, ook katten, apen en vleermuizen. Meestal raak je besmet door een beet, krab of lik in een open wond. Het komt bijna niet voor dat mensen elkaar besmetten.",
      },
      {
        p: "Rabiës begint grieperig met koorts. Daarna krijg je spierkramp, stuipen en verlammingen, en slik- en ademhalingsproblemen. Je kunt ook bang zijn voor water. Zonder tijdige actie overlijd je aan rabiës.",
      },
      {
        ul: [
          "Vermijd contact met zoogdieren, vooral honden, katten, apen en vleermuizen.",
          "Aai of voer geen zoogdieren.",
          "Blijf minimaal een armlengte afstand.",
          "Raak geen zieke of dode dieren aan.",
          "Vermijd grotten: daar zijn vaak vleermuizen.",
        ],
      },
      {
        p: "Ben je gebeten, gekrabd of over een open wond gelikt: maak de wond minstens 15 minuten schoon met lauwwarm water en zeep en ontsmet daarna met 70% alcohol. Neem direct contact op met de alarmcentrale van je zorg- of reisverzekering en ga naar een arts. Om rabiës te voorkomen kunnen vaccinaties en antiserum (MARIG) nodig zijn. De behandeling moet zo snel mogelijk starten. In veel landen is antiserum niet verkrijgbaar; dan moet je soms terugvliegen.",
      },
      {
        p: "Een rabiësvaccinatieserie bestaat uit twee vaccinaties die levenslange basisbescherming geven. Als je voor je reis gevaccineerd bent, heb je na een beet of krab meestal nog twee extra vaccinaties nodig. Die zijn bijna overal ter wereld verkrijgbaar, zodat je waarschijnlijk niet je vakantie hoeft af te breken. Vraag bij een reizigersvaccinatiebureau of een vaccinatie voor jouw reis nodig is.",
      },
    ],
  },
  schistosomiasis: {
    updated: "2025-08-07",
    blocks: [
      {
        p: "Schistosomiasis (eerder ook bilharzia genoemd) is een infectie met een parasitaire worm. Er zijn vijf soorten, waaronder Schistosoma mansoni, haematobium, japonicum, intercalatum en mekong.",
      },
      {
        p: "Je kunt schistosomiasis krijgen door contact met zoet water in de tropen en subtropen, bijvoorbeeld zwemmen of pootjebaden.",
      },
      {
        p: "De larve van de worm komt het lichaam binnen door de huid. In de bloedvaten van darm of blaas worden de larven volwassen en leggen ze eieren. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "Soms merk je niets, soms pas maanden tot jaren later. Als de larve door de huid gaat, kun je jeukende huiduitslag krijgen. Na een paar weken kun je koorts krijgen, plus rugpijn, buikpijn, diarree, bloed bij ontlasting of urine, of verlammingen, door ontstekingen waar de wormen eieren hebben gelegd.",
      },
      {
        p: "Ga niet zwemmen of pootjebaden in zoet water in landen waar schistosomiasis voorkomt. Ben je toch in het water geweest, droog je dan snel af in plaats van het water te laten opdrogen. Je kunt je niet laten vaccineren, maar er zijn wel tabletten om de ziekte te behandelen. Chloorbaden en de zee zijn wel veilig.",
      },
    ],
  },
  tekenencefalitis: {
    updated: "2024-05-02",
    blocks: [
      {
        p: "Tekenencefalitis is een infectieziekte door een virus dat een hersen(vlies)ontsteking kan geven. In het Duits heet de ziekte FSME, in het Engels tick-borne encephalitis (TBE).",
      },
      {
        p: "Je kunt tekenencefalitis krijgen in bossen in Europa, Rusland en bepaalde gebieden in Centraal-Azië. De ziekte komt sinds een aantal jaar ook in Nederland voor.",
      },
      {
        p: "Kleine wilde zoogdieren, vooral knaagdieren, kunnen het virus hebben. Teken (Ixodes ricinus en Ixodes persulcatus) besmetten dieren en soms mensen. Van Ixodes ricinus kun je ook de ziekte van Lyme krijgen. Teken leven in bossen, duinen, heide, weilanden, parken en tuinen. Je kunt geen andere mensen besmetten.",
      },
      {
        p: "De meeste mensen worden bijna niet ziek; sommigen worden ernstig ziek met een hersen(vlies)ontsteking. Meestal voel je je eerst grieperig, daarna hoofdpijn, vermoeidheid, misselijkheid en overgeven. Soms volgen zware hoofdpijn, geheugenverlies, verlammingen of een verminderd bewustzijn.",
      },
      {
        p: "Voorkom tekenbeten met kleren die de huid bedekken en een insectenwerend middel. Verwijder een teek zo snel mogelijk met een tekentang: besmetting kan al snel na de beet. Je kunt je laten vaccineren; of dat nodig is hangt af van waar je heen gaat, hoelang en wat je gaat doen. Vraag een reizigersvaccinatiebureau om advies.",
      },
    ],
  },
  tuberculose: {
    updated: "2024-04-04",
    blocks: [
      {
        p: "Tuberculose (tbc) is een infectieziekte door de bacterie Mycobacterium tuberculosis (tuberkelbacterie).",
      },
      {
        p: "Je kunt in veel landen tuberculose krijgen, vooral in Azië, Afrika, Zuid-Amerika en Oost-Europa.",
      },
      {
        p: "Tuberculose is vaak longtuberculose, maar er zijn ook vormen in nieren, gewrichten en klieren. Alleen mensen met longtuberculose zijn besmettelijk als zij hoesten en er bacteriën in de lucht komen. Door die bacteriën in te ademen kun je besmet worden.",
      },
      {
        p: "Niet iedereen die de bacterie inademt wordt ziek. De bacterie kan lang in het lichaam blijven zonder klachten. Iemand met een normale weerstand heeft na infectie ongeveer 10% kans om de ziekte tuberculose te ontwikkelen. Klachten, als ze komen, zijn moeheid, hoesten, gewichtsverlies en weinig eetlust. Dat kan na zes weken, maar ook pas na jaren.",
      },
      {
        p: "Er is een vaccin tegen tuberculose. Of vaccinatie nodig is, hangt af van waar je heen gaat, hoelang en wat je gaat doen. Dat bepalen professionals van de tuberculose-afdeling. Vraag bij een reizigersvaccinatiebureau of een doorverwijzing nodig is. Draai je hoofd af van iemand die hoest om de kans op besmetting kleiner te maken.",
      },
    ],
  },
};

const en: Record<string, DiseaseProfile> = {
  buiktyfus: {
    updated: "2024-04-02",
    blocks: [
      {
        p: "Typhoid is an infection caused by the bacterium Salmonella typhi.",
      },
      {
        p: "You can get typhoid in Asia and Africa. It also occurs in Central and South America, but the risk there is low. It is rare in the Netherlands.",
      },
      {
        p: "You can get typhoid by eating food or drinking water contaminated with Salmonella typhi. Food or water can become contaminated with faeces from an infected person, even if this is not visible. If an infected person does not wash their hands after using the toilet and then prepares your food, you can catch typhoid. Undercooked meat, chicken or eggs, and raw dairy products, can also transmit the disease. If you have typhoid, you can infect other people.",
      },
      {
        p: "Symptoms include fever, headache, abdominal pain and loss of appetite. You may have diarrhoea alternating with constipation. Later you can become drowsy and develop red spots on the abdomen. You can become very ill and die from the disease.",
      },
      {
        p: "Vaccination can be wise. Whether you need it depends on your health, where you go and how long you stay. Ask a travel clinic whether vaccination is needed for your trip.",
      },
      {
        p: "Vaccination does not protect completely. Good hygiene, especially when preparing food, lowers the risk. Wash your hands with soap regularly, especially after using the toilet. Drink only boiled water or bottled water, and eat food that has been prepared thoroughly and cleanly.",
      },
    ],
  },
  chikungunya: {
    updated: "2026-04-30",
    blocks: [
      {
        p: "Chikungunya is a viral infectious disease. It is very similar to dengue (breakbone fever).",
      },
      {
        p: "You can get chikungunya in urban areas in many countries with a warm or (sub)tropical climate.",
      },
      {
        p: "You get the disease if you are bitten by a mosquito carrying chikungunya. That happens mainly during the day, between sunrise and sunset. You cannot infect other people.",
      },
      {
        p: "If you are pregnant you can pass the virus to your baby, especially just before or during birth. The baby can then become seriously ill. Travel in the last trimester to a country with a large chikungunya outbreak is advised against.",
      },
      {
        p: "Chikungunya resembles flu, with fever, headache, muscle and back pain, nausea and vomiting. You can also get a rash and joint pain. Symptoms usually last one to three weeks. Joint pain can sometimes last months to years. Death is rare. After recovery you cannot get chikungunya from the same virus again.",
      },
      {
        p: "Keep mosquitoes away during the day. In the Netherlands, vaccination against chikungunya has been possible since April 2025. For some travellers it can be useful. Ask a travel clinic what is possible for you.",
      },
    ],
  },
  dengue: {
    updated: "2026-08-13",
    blocks: [
      {
        p: "Dengue is a viral infectious disease. It is also called breakbone fever.",
      },
      {
        p: "You can get dengue in urban areas in many countries with a warm or (sub)tropical climate. Risk rises in the rainy season, when large outbreaks can occur. Dengue infections have increased worldwide over the past 50 years.",
      },
      {
        p: "You get the disease if you are bitten by a mosquito carrying the dengue virus. That happens mainly during the day. You cannot infect other people.",
      },
      {
        p: "Dengue usually resembles a mild flu, with fever, headache (behind the eyes), muscle pain and joint pain. A rash can appear after a few days. Rarely the disease is severe, with bleeding, and hospital care may be needed. The chance of severe disease is slightly higher if you have had dengue before.",
      },
      {
        p: "Keep mosquitoes away during the day. There is a dengue vaccine for adults and children older than four. Ask a travel clinic what is possible for you.",
      },
    ],
  },
  dtp: {
    updated: "2023-12-07",
    blocks: [
      { p: "DTP stands for diphtheria, tetanus and polio." },
      { h: "Diphtheria" },
      {
        p: "Diphtheria is an infectious disease caused by the bacterium Corynebacterium diphtheriae. You can get it from people who carry the bacterium in their throat or on their skin, through coughing or wound fluid.",
      },
      {
        p: "The bacterium produces toxins that swell the throat and make breathing difficult. If the toxins enter the blood, they can inflame the heart and you can die. A skin infection is less serious.",
      },
      { h: "Tetanus" },
      {
        p: "Tetanus is an infectious disease caused by Clostridium tetani. The bacterium lives in soil and dirt. It enters through a wound or mucous membranes. You cannot infect other people.",
      },
      {
        p: "If the bacterium enters a wound, toxins can cause muscle cramps throughout the body. Cramps in the breathing muscles can be fatal.",
      },
      { h: "Polio" },
      {
        p: "Polio (infantile paralysis) is caused by poliovirus. You can get it from other people, via faeces or through food and drink contaminated with faeces.",
      },
      {
        p: "There are usually no symptoms. Sometimes you feel flu-like. A small share of infections cause temporary paralysis, usually in the legs, sometimes in the breathing muscles. Rarely the paralysis is permanent: in about one in two hundred people who get polio.",
      },
      {
        p: "Almost everyone born in the Netherlands after 1950 was vaccinated as a child. If your last vaccination was more than ten years ago, get a DTP booster. Ask a travel clinic for advice.",
      },
    ],
  },
  "gele-koorts": {
    updated: "2026-03-16",
    blocks: [
      { p: "Yellow fever is an infectious disease caused by a virus." },
      {
        p: "You can get yellow fever in certain parts of Africa and South America.",
      },
      {
        p: "Humans and monkeys can carry the virus. You get it if you are bitten by a mosquito. You cannot infect other people.",
      },
      {
        p: "You may feel flu-like with fever, and sometimes jaundice, when your skin and the whites of your eyes turn yellow. You can also become seriously ill and die. Between 3 and 20 percent of people who get yellow fever die from the disease.",
      },
      {
        p: "You can be vaccinated against yellow fever. For some countries the vaccination is required. If it is required, you must have received it at least ten days before arrival. Even when it is not required, LCR advises vaccination if you travel to an area where yellow fever occurs.",
      },
      {
        p: "Not every travel clinic is allowed to give yellow fever vaccine: only clinics that meet legal requirements. Always ask a travel clinic for advice.",
      },
    ],
  },
  "hepatitis-a": {
    updated: "2026-07-09",
    blocks: [
      {
        p: "Hepatitis A is an inflammation of the liver caused by the hepatitis A virus. The virus is highly contagious.",
      },
      {
        p: "You can get hepatitis A in Africa, Central and South America and Asia, and also in countries in Eastern Europe. It is rare in the Netherlands.",
      },
      {
        p: "You can get hepatitis A by drinking water or eating food contaminated with faeces from someone who has hepatitis A. The virus can also spread via contaminated door handles, taps and towels. You can infect other people indirectly.",
      },
      {
        p: "Adults and older children can feel tired and nauseous, with fever, abdominal pain and loss of appetite. After a few days jaundice can appear: the skin and whites of the eyes turn yellow. Symptoms usually last a few weeks, sometimes up to three months. Young children often do not become very ill, but they can still spread the virus.",
      },
      {
        p: "For most tropical and subtropical countries, and for some countries in Eastern Europe and around the Mediterranean, vaccination is wise. Ask a travel clinic whether you need it for your trip.",
      },
      { p: "You can lower the risk of hepatitis A with these measures:" },
      { h: "Hygiene" },
      {
        ul: [
          "Keep things as clean as possible and wash your hands with soap regularly, especially after using the toilet and before eating.",
        ],
      },
      { h: "Food and drink" },
      {
        ul: [
          "Drink only sealed bottled water or boiled water.",
          "Avoid ice cubes: they are often made from tap water.",
          "Eat only packaged ice cream.",
          "Avoid raw vegetables and salads: they are often washed with tap water.",
          "Peel fruit yourself.",
          "Eat only fresh meat or fish, including shellfish, that has been thoroughly cooked and is still hot.",
        ],
      },
      {
        p: "Because hepatitis A is highly contagious, these measures are not always enough.",
      },
    ],
  },
  "hepatitis-b": {
    updated: "2024-05-02",
    blocks: [
      {
        p: "Hepatitis B is an inflammation of the liver caused by the hepatitis B virus.",
      },
      {
        p: "Hepatitis B occurs worldwide, especially in Africa, Asia, Eastern Europe and Latin America.",
      },
      { p: "You can get the disease through:" },
      {
        ul: [
          "sex without a condom or other protection",
          "contact with blood, for example via injecting drug use",
          "a medical treatment that is not sterile, for example at the dentist",
          "tattoos and piercings",
          "shaving with a blade that has already been used",
        ],
      },
      {
        p: "In countries where hepatitis B is more common, children can catch it at birth from their mother. If you are infected, you can infect others through unprotected sex or blood contact.",
      },
      {
        p: "After infection, people often have few or no symptoms, but they can still infect others. Symptoms, if they appear, usually come two to six months later: a flu-like feeling and sometimes jaundice. Some people remain infected long term. They often notice nothing, but have a higher chance of liver cirrhosis or liver cancer.",
      },
      {
        p: "A long stay in an area with a lot of hepatitis B raises the risk. In that case you can get a vaccine. Ask a travel clinic whether vaccination is needed for your trip.",
      },
    ],
  },
  hoogteziekte: {
    updated: "2026-03-16",
    blocks: [
      {
        p: "If you ascend too quickly to high altitude without acclimatising, you can get altitude sickness. The body then does not have enough time to adapt to the low oxygen level in the air.",
      },
      {
        p: "This usually happens only above 2,500 metres. Whether you get altitude sickness varies by person and is not related to how fit you are. If you have had it before, you are more likely to get it again.",
      },
      { p: "There are two forms: mild and severe." },
      {
        ul: [
          "The mild form starts a few hours or a day after you arrive at high altitude. You are short of breath, have a headache, feel nauseous and may vomit. You feel dizzy and weak. Symptoms usually last a few days, but can progress to the severe form.",
          "In the severe form, headache and shortness of breath get worse, including at rest and especially at night. You can become so weak that you need help dressing and eating. Walking may look as if you are drunk. Without treatment you can develop fluid in the lungs and brain, fall into a coma and die.",
        ],
      },
      { p: "You can lower the risk:" },
      {
        ul: [
          "Ascend slowly above 2,500 metres.",
          "Give your body two days to acclimatise when going higher than 2,500 metres.",
          "Do not use alcohol or sleeping pills.",
          "Preferably do not sleep more than 300 metres higher than the night before.",
          "In consultation with a doctor you can use acetazolamide if you have had altitude sickness before, or if you must ascend suddenly to a sleeping altitude above 3,500 metres without time to acclimatise.",
        ],
      },
      {
        p: "For the mild form: do not go higher, or descend, if you are not better within a few days or if it gets worse. Paracetamol can help a headache. For the severe form, descend immediately. That can save your life. A clinician can give medicine or oxygen.",
      },
    ],
  },
  "japanse-encefalitis": {
    updated: "2023-12-14",
    blocks: [
      {
        p: "Japanese encephalitis is an infectious disease caused by a virus.",
      },
      {
        p: "You can get Japanese encephalitis in rural Asia and in parts of Australia.",
      },
      {
        p: "You get the disease if you are bitten by a mosquito carrying Japanese encephalitis. Those mosquitoes bite in the evening and at night. You cannot infect other people.",
      },
      {
        p: "The disease is usually mild and resembles flu with fever, headache and gut symptoms. Rarely it is worse and causes inflammation of the brain (encephalitis). Then there is a chance of disability or death.",
      },
      {
        p: "Keep mosquitoes away in the evening and at night with repellent, covering clothes and a mosquito net. There is a vaccine, but you only receive it when you are at prolonged risk. Ask a travel clinic whether vaccination is needed for your trip.",
      },
    ],
  },
  malaria: {
    updated: "2026-04-13",
    blocks: [
      { p: "Malaria is an infectious disease caused by a malaria parasite." },
      {
        p: "You can get malaria in (sub)tropical areas of Africa, Asia and Central and South America.",
      },
      {
        p: "If you are bitten by a mosquito carrying malaria, parasites can enter your body. Those mosquitoes bite in the evening and at night, between sunset and sunrise. The parasites multiply first in the liver and then in red blood cells. You cannot infect other people.",
      },
      {
        p: "Malaria starts with fever, fatigue, headache and muscle pain. The fever is not always high and you do not always shiver. It can resemble flu. You may also have diarrhoea, vomiting, coughing or shortness of breath.",
      },
      { p: "There are five forms of malaria:" },
      {
        ul: [
          "Falciparum malaria (malaria tropica), caused by Plasmodium falciparum, is the most dangerous. You can die within a few days. It is generally treatable if treatment starts in time.",
          "Plasmodium vivax, Plasmodium ovale and Plasmodium malariae are less dangerous. Symptoms are similar, but death is rare.",
          "Plasmodium knowlesi mainly affects macaques, but is sometimes found in people. People can die from this form.",
        ],
      },
      {
        p: "You can develop falciparum malaria up to about four weeks after infection. Most malaria tablets therefore need to be continued for at least four weeks after leaving a malaria area. Atovaquone/proguanil only needs to be continued for one week after leaving. If you become ill, tell the doctor you were recently abroad.",
      },
      {
        p: "There is no effective malaria vaccine. Keep mosquitoes away. In higher-risk areas the advice is also to take tablets (chemoprophylaxis), or sometimes to take emergency treatment with you. Ask a travel clinic for advice.",
      },
    ],
  },
  mazelen: {
    updated: "2026-07-22",
    blocks: [
      {
        p: "Measles is a highly contagious infectious disease caused by the measles virus. One person with measles can infect 12 to 18 others.",
      },
      {
        p: "Measles occurs worldwide. In most countries children are vaccinated against it.",
      },
      {
        p: "You can catch the virus through the air from someone who coughs or sneezes. The virus can stay in the air for up to two hours after someone has left a room. Keeping distance is not enough. Fresh air lowers the chance but does not remove it.",
      },
      {
        p: "Measles usually starts with fever. You feel unwell, have a cold and cough. Small white spots appear on the lining of the mouth. After three to seven days spots appear behind the ears and then over the whole body. Sometimes the disease is more serious, with pneumonia or brain inflammation, and you can die from it.",
      },
      {
        p: "Most people in the Netherlands were vaccinated as children in the national programme, or have had measles. If you are not vaccinated and have never had measles, it is wise to get the MMR vaccine (mumps, measles and rubella).",
      },
      {
        p: "If you were born between 1965 and 1975 you have a higher chance of measles: you were born before measles vaccination was in the national programme. If you travel to a country with more measles, it is wise to get MMR.",
      },
      {
        p: "Children younger than fourteen months have not yet had MMR. If they travel to a country with more measles than the Netherlands, they can in some cases receive MMR from 6 months of age. Ask a child health clinic or travel clinic for advice.",
      },
    ],
  },
  meningokokken: {
    updated: "2024-04-04",
    blocks: [
      {
        p: "Meningococcal meningitis is a disease caused by the bacterium Neisseria meningitidis (meningococcus). The best-known types are A, B, C, W and Y.",
      },
      {
        p: "The disease occurs worldwide. In sub-Saharan Africa there are epidemics in the dry season, mainly type A and increasingly type W135. Outbreaks also occur elsewhere from time to time.",
      },
      {
        p: "You can catch it through the air from someone who talks, coughs or sneezes, or through direct contact such as kissing on the mouth.",
      },
      {
        p: "If you become ill, it is usually a few days after infection. It starts like flu with high fever, muscle pain, vomiting, headache and a stiff neck: pain when you bend your head. The disease can be very serious, and sometimes fatal.",
      },
      {
        p: "There is a vaccine against types A, C, W135 and Y (ACW135Y). Since May 2018, children in the Netherlands receive this vaccination at 14 months and 14 years. Ask a travel clinic whether vaccination is needed, for example for a longer stay in a country where meningitis is common. For travellers on pilgrimage to Mecca this vaccination is required.",
      },
    ],
  },
  muggen: {
    updated: "2025-01-27",
    blocks: [
      {
        p: "Mosquitoes can infect you with diseases if you are bitten while travelling.",
      },
      {
        p: "Diseases you can get from mosquitoes include yellow fever, malaria, dengue, Japanese encephalitis, West Nile virus, chikungunya, Eastern equine encephalitis, Barmah Forest virus, Murray Valley encephalitis, Ross River virus, Saint Louis encephalitis, Zika and Oropouche virus.",
      },
      {
        p: "Which mosquito-borne diseases matter for your trip is in the LCR advice per country. Keep mosquitoes away with repellent, covering clothing and a mosquito net where needed.",
      },
    ],
  },
  pest: {
    updated: "2023-12-04",
    blocks: [
      {
        p: "Plague is a contagious disease caused by the bacterium Yersinia pestis. There are three forms: bubonic plague, pneumonic plague and sepsis (blood poisoning). Which form you get depends on how you are infected.",
      },
      { p: "You can get plague in Africa, North and South America and Asia." },
      {
        p: "Most people get plague from the bite of an infected flea. You can also get it through contact with infected (rodent) animals or people. Pneumonic plague can follow if you inhale the bacterium when an infected person coughs or sneezes.",
      },
      {
        p: "It is usually bubonic plague: fever, chills, headache and painful swellings in the groin, armpits or neck. Without treatment you can get blood poisoning; the chance of dying from that is high. Pneumonic plague is the most serious form and resembles severe pneumonia.",
      },
      {
        p: "There is no vaccine against plague. Avoid contact with sick or dead animals. If you have pets, keep them free of fleas.",
      },
    ],
  },
  rabies: {
    updated: "2025-08-07",
    blocks: [
      {
        p: "Rabies is a rare, fatal infectious disease caused by a virus.",
      },
      { p: "You can get rabies in many countries." },
      {
        p: "You can get rabies from the saliva of infected mammals: not only dogs, but also cats, monkeys and bats. Infection is usually through a bite, scratch or lick on an open wound. Person-to-person infection is extremely rare.",
      },
      {
        p: "Rabies starts like flu with fever. Then come muscle cramps, seizures and paralysis, and problems swallowing and breathing. You can also develop a fear of water. Without timely action, rabies is fatal.",
      },
      {
        ul: [
          "Avoid contact with mammals, especially dogs, cats, monkeys and bats.",
          "Do not stroke or feed mammals.",
          "Stay at least an arm’s length away.",
          "Do not touch sick or dead animals.",
          "Avoid caves: bats are often present.",
        ],
      },
      {
        p: "If you are bitten, scratched or licked on an open wound: wash the wound for at least 15 minutes with lukewarm water and soap, then disinfect with 70% alcohol. Contact your health or travel insurer’s emergency line immediately and see a doctor. Vaccinations and antiserum (MARIG) may be needed. Treatment must start as soon as possible. In many countries antiserum is not available; you may then need to fly back.",
      },
      {
        p: "A rabies vaccine series is two doses that give lifelong basic protection. If you were vaccinated before travel, you usually need two extra doses after a bite or scratch. Those extra doses are available in most of the world, so you probably will not have to cut your trip short. Ask a travel clinic whether vaccination is needed for your trip.",
      },
    ],
  },
  schistosomiasis: {
    updated: "2025-08-07",
    blocks: [
      {
        p: "Schistosomiasis (formerly also called bilharzia) is an infection with a parasitic worm. There are five species, including Schistosoma mansoni, haematobium, japonicum, intercalatum and mekong.",
      },
      {
        p: "You can get schistosomiasis through contact with fresh water in the tropics and subtropics, for example swimming or paddling.",
      },
      {
        p: "The worm larva enters the body through the skin. In the blood vessels of the gut or bladder the larvae mature and lay eggs. You cannot infect other people.",
      },
      {
        p: "Sometimes you notice nothing, sometimes only months to years later. When the larva enters the skin you can get an itchy rash. After a few weeks you can get fever, plus back pain, abdominal pain, diarrhoea, blood in stool or urine, or paralysis, from inflammation where the worms have laid eggs.",
      },
      {
        p: "Do not swim or paddle in fresh water in countries where schistosomiasis occurs. If you have been in the water, dry off quickly rather than letting the water dry on your skin. There is no vaccine, but there are tablets to treat the disease. Chlorinated pools and the sea are safe.",
      },
    ],
  },
  tekenencefalitis: {
    updated: "2024-05-02",
    blocks: [
      {
        p: "Tick-borne encephalitis is a viral infectious disease that can cause inflammation of the brain or its lining. In German it is called FSME; in English, tick-borne encephalitis (TBE).",
      },
      {
        p: "You can get tick-borne encephalitis in forests in Europe, Russia and parts of Central Asia. It has also occurred in the Netherlands for several years.",
      },
      {
        p: "Small wild mammals, especially rodents, can carry the virus. Ticks (Ixodes ricinus and Ixodes persulcatus) infect animals and sometimes people. Ixodes ricinus can also transmit Lyme disease. Ticks live in forests, dunes, heath, meadows, parks and gardens. You cannot infect other people.",
      },
      {
        p: "Most people barely become ill; some become seriously ill with inflammation of the brain or meninges. You usually feel flu-like at first, then headache, fatigue, nausea and vomiting. Sometimes severe headache, memory loss, paralysis or reduced consciousness follow.",
      },
      {
        p: "Prevent tick bites with clothing that covers the skin and an insect repellent. Remove a tick as soon as possible with a tick remover: infection can start soon after the bite. You can be vaccinated; whether you need it depends on where you go, how long and what you do. Ask a travel clinic for advice.",
      },
    ],
  },
  tuberculose: {
    updated: "2024-04-04",
    blocks: [
      {
        p: "Tuberculosis (TB) is an infectious disease caused by the bacterium Mycobacterium tuberculosis.",
      },
      {
        p: "You can get tuberculosis in many countries, especially in Asia, Africa, South America and Eastern Europe.",
      },
      {
        p: "Tuberculosis is often pulmonary TB, but there are also forms in the kidneys, joints and glands. Only people with pulmonary TB are contagious when they cough and bacteria enter the air. You can become infected by inhaling those bacteria.",
      },
      {
        p: "Not everyone who inhales the bacterium becomes ill. It can stay in the body for a long time without symptoms. Someone with normal immunity has about a 10% chance of developing tuberculosis after infection. Symptoms, if they appear, include fatigue, coughing, weight loss and loss of appetite. That can happen after six weeks, or only after years.",
      },
      {
        p: "There is a vaccine against tuberculosis. Whether you need it depends on where you go, how long and what you do. Tuberculosis department professionals can decide this. Ask a travel clinic whether a referral is needed. Turning your head away from someone who is coughing lowers the chance of infection.",
      },
    ],
  },
};

export function diseaseProfile(
  slug: string,
  locale: Locale,
): DiseaseProfile | undefined {
  const pack = locale === "nl" ? nl : en;
  return pack[slug] ?? nl[slug];
}

export function diseaseWhat(profile: DiseaseProfile): string {
  const first = profile.blocks.find((block) => "p" in block);
  return first && "p" in first ? first.p : "";
}

export function profileToMarkdown(profile: DiseaseProfile): string {
  return profile.blocks
    .map((block) => {
      if ("h" in block) return `### ${block.h}`;
      if ("ul" in block) return block.ul.map((item) => `- ${item}`).join("\n");
      return block.p;
    })
    .join("\n\n");
}
